import {
    Home,
    Refresh
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    Container,
    Link,
    Paper,
    Typography
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { getOrDefault } from "../../../Helpers/Utils";
import { useMicrospecThemeLanguageContext } from "../../../Themes";
import NotFoundSvg from "./NotFoundSvg";

// Styled Components
const StyledContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: `
        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%),
        linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)
    `,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
}));

const GlowingCard = styled(Paper)(({ theme }) => ({
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '24px',
    padding: theme.spacing(6),
    textAlign: 'center',
    position: 'relative',
    zIndex: 10,
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',

    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 35px 70px rgba(0, 0, 0, 0.3)',
    }
}));

const AnimatedButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
    borderRadius: '50px',
    padding: '12px 32px',
    fontSize: '1.1rem',
    fontWeight: 700,
    textTransform: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    ...(buttonVariant === 'primary' && {
        background: 'linear-gradient(45deg, #667eea, #764ba2)',
        color: 'white',
        border: 'none',
        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',

        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            transition: 'left 0.5s ease',
        },

        '&:hover': {
            transform: 'translateY(-3px) scale(1.05)',
            boxShadow: '0 15px 35px rgba(102, 126, 234, 0.6)',

            '&::before': {
                left: '100%',
            }
        }
    }),

    ...(buttonVariant === 'secondary' && {
        background: 'rgba(255, 255, 255, 0.1)',
        color: 'white',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backdropFilter: 'blur(10px)',

        '&:hover': {
            background: 'rgba(255, 255, 255, 0.2)',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            transform: 'translateY(-2px)',
        }
    })
}));


const NotFoundElement = ({ spec, type, index }) => {
    const { localizationProvider } = useMicrospecThemeLanguageContext();

    const componentSpec = getOrDefault(spec, {});
    const title = getOrDefault(componentSpec.title, localizationProvider.translateByType({
        type: "Internal",
        text: "notFoundTitle"
    }));
    const subtitle = getOrDefault(componentSpec.subtitle, localizationProvider.translateByType({
        type: "Internal",
        text: "notFoundSubtitle"
    }));
    const quickLinks = getOrDefault(componentSpec.quickLinks, [
        { label: localizationProvider.translateByType({ type: "Internal", text: "notFoundHome" }), link: "/" }
    ]);
    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.8
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 15,
                duration: 0.6
            }
        }
    };

    return (
        <StyledContainer>
            <Container maxWidth="md">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <GlowingCard elevation={0}>

                        {/* Main Title */}
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant="h3"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: { xs: '2rem', md: '3rem' },
                                    mb: 2,
                                    textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                }}
                            >
                                {title}
                            </Typography>
                        </motion.div>

                        {/* Animated SVG */}
                        <motion.div
                            variants={itemVariants}
                            sx={{ mb: 4 }}
                        >
                            <Box
                                sx={{
                                    alignContent: 'center',
                                    justifyContent: 'center',
                                    display: 'flex',
                                    textAlign: 'center',
                                    filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))',
                                    '& svg': {
                                        maxWidth: '100%',
                                        height: 'auto',
                                    }
                                }}
                            >
                                <motion.div
                                    animate={{
                                        y: [0, -10, 0],
                                        scale: [1, 1.02, 1],
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                >
                                    <NotFoundSvg />
                                </motion.div>
                            </Box>
                        </motion.div>

                        {/* Subtitle */}
                        <motion.div variants={itemVariants}>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontWeight: 400,
                                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                                    mb: 4,
                                    lineHeight: 1.6,
                                    maxWidth: '600px',
                                    mx: 'auto',
                                }}
                            >
                                {subtitle}
                            </Typography>
                        </motion.div>

                        {/* Action Buttons */}
                        <motion.div variants={itemVariants}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    justifyContent: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: 'center',
                                    mt: 4
                                }}
                            >
                                {/* Home Button */}
                                <Link href="/" style={{ textDecoration: 'none' }}>
                                    <AnimatedButton
                                        variant="primary"
                                        startIcon={<Home sx={{ mx: 0.5 }} />}
                                        size="large"
                                    >
                                        {localizationProvider.translateByType({
                                            type: "Internal",
                                            text: "notFoundBackToHome"
                                        })}
                                    </AnimatedButton>
                                </Link>

                                {/* Refresh Button */}
                                <AnimatedButton
                                    variant="secondary"
                                    startIcon={<Refresh sx={{ mx: 0.5 }} />}
                                    size="large"
                                    onClick={() => window.location.reload()}
                                >
                                    {localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "notFoundTryAgain"
                                    })}
                                </AnimatedButton>
                            </Box>
                        </motion.div>

                        {/* Error Code */}
                        <motion.div variants={itemVariants}>
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                    }}
                                >
                                    {localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "notFoundErrorCode"
                                    })}
                                </Typography>
                            </Box>
                        </motion.div>

                        {/* Quick Links */}
                        {quickLinks.length > 0 && <motion.div variants={itemVariants}>
                            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.6)',
                                        mb: 2,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {localizationProvider.translateByType({
                                        type: "Internal",
                                        text: "notFoundQuickNavigation"
                                    })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    {quickLinks.map((item, index) => (
                                        <motion.div
                                            key={item.label}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <Chip
                                                label={item.label}
                                                clickable
                                                component="a"
                                                href={`${item.link}`}
                                                sx={{
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        background: 'rgba(255, 255, 255, 0.1)',
                                                        color: 'white',
                                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                                    }
                                                }}
                                            />
                                        </motion.div>
                                    ))}
                                </Box>
                            </Box>
                        </motion.div>}
                    </GlowingCard>
                </motion.div>
            </Container>
        </StyledContainer>
    );
};

export default NotFoundElement;
